import { AxiosResponse } from 'axios';

import { ApiResponseDto } from 'interfaces/apiResponse';

/**
 * A rejected invoicing request, carrying the backend's stable error `code` (meant for i18n) and its
 * optional `details`. The backend `message` is English, for developers only, so it is kept on
 * `backendMessage` and never shown; the one exception (AADE's own text) travels on the invoice.
 */
export class InvoicingApiError extends Error {
  constructor(
    readonly code: string,
    readonly status?: number,
    readonly details?: Record<string, unknown>,
    readonly backendMessage?: string,
  ) {
    super(code);
    this.name = 'InvoicingApiError';
  }
}

export const isInvoicingApiError = (error: unknown): error is InvoicingApiError => error instanceof InvoicingApiError;

export const hasInvoicingErrorCode = (error: unknown, ...codes: string[]): boolean =>
  isInvoicingApiError(error) && codes.includes(error.code);

/**
 * Whether a failed request may still have taken effect: no envelope error came back (timeout,
 * network failure) or the backend failed talking to AADE. Validation refusals (4xx) change
 * nothing, so only these call for re-reading the object before showing it again.
 */
export const isUncertainOutcome = (error: unknown): boolean => !isInvoicingApiError(error) || (error.status ?? 0) >= 500;

interface HttpErrorLike {
  response?: { status?: number; data?: ApiResponseDto<unknown> };
}

/**
 * Converts an axios error whose body follows the `{"errors": [...]}` envelope into an
 * `InvoicingApiError` built from its first error; anything else (network failure, timeout) is
 * returned unchanged.
 */
export const toInvoicingApiError = (error: unknown): unknown => {
  const response = (error as HttpErrorLike | undefined)?.response;
  const firstError = response?.data?.errors?.[0];

  if (!firstError?.code) return error;

  return new InvoicingApiError(firstError.code, response?.status, firstError.details, firstError.message);
};

/** Throws the envelope error of a response that came back with `errors` instead of `data`. */
export const unwrapInvoicingResponse = <T>(data: ApiResponseDto<T>): T => {
  const firstError = data.errors?.[0];
  if (firstError) {
    throw new InvoicingApiError(firstError.code, undefined, firstError.details, firstError.message);
  }

  return data.data!;
};

/** Runs an invoicing request, unwrapping `data` and turning every envelope error into an `InvoicingApiError`. */
export const invoicingRequest = async <T>(call: () => Promise<AxiosResponse<ApiResponseDto<T>>>): Promise<T> => {
  try {
    const response = await call();
    return unwrapInvoicingResponse(response.data);
  } catch (error) {
    throw toInvoicingApiError(error);
  }
};
